import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { CreateBusinessLinePayload } from './dtos/create.dto';
import type { GetAllBusinessLinesParams } from './dtos/getAll.dto';
import type { UpdateBusinessLinePayload } from './dtos/update.dto';
import type { BusinessLineRepository } from './repository';
import type {
  CreateBusinessLineOutput,
  CreateBusinessLineResponse,
} from './types/create.type';
import type {
  GetAllBusinessLinesOutput,
  GetAllBusinessLinesResponse,
} from './types/getAll.type';
import type {
  GetAllBusinessLinesToSelectOutput,
  GetAllBusinessLinesToSelectResponse,
} from './types/getAllToSelect.type';
import type {
  GetBusinessLineByIdOutput,
  GetBusinessLineByIdResponse,
} from './types/getById.type';
import type {
  UpdateBusinessLineOutput,
  UpdateBusinessLineResponse,
} from './types/update.type';

class BusinessLineService implements BusinessLineRepository {
  public async create(
    payload: CreateBusinessLinePayload,
  ): Promise<CreateBusinessLineOutput> {
    const { data } = await backendHttpClient.post<
      CreateBusinessLineResponse,
      CreateBusinessLinePayload
    >({ path: 'business-lines', body: payload });

    return { id: data.businessLine.id };
  }

  public async getAllToSelect(): Promise<GetAllBusinessLinesToSelectOutput> {
    const { data } =
      await backendHttpClient.get<GetAllBusinessLinesToSelectResponse>({
        path: 'business-lines/select',
      });

    return {
      list: data.list.map(bl => ({
        label: bl.translations.en,
        type: bl.type,
        value: bl.id,
      })),
    };
  }

  public async getAll(
    params: GetAllBusinessLinesParams,
  ): Promise<GetAllBusinessLinesOutput> {
    const { data } = await backendHttpClient.get<
      GetAllBusinessLinesResponse,
      GetAllBusinessLinesParams
    >({
      path: 'business-lines',
      params: {
        page: params.page,
        perPage: params.perPage,
      },
    });

    return data;
  }

  public async getById(id: string): Promise<GetBusinessLineByIdOutput> {
    const { data } = await backendHttpClient.get<GetBusinessLineByIdResponse>({
      path: `business-lines/${id}`,
    });

    return {
      businessLine: data.businessLine,
    };
  }

  public async update(
    payload: UpdateBusinessLinePayload,
  ): Promise<UpdateBusinessLineOutput> {
    const { data } = await backendHttpClient.patch<
      UpdateBusinessLineResponse,
      UpdateBusinessLinePayload
    >({ body: payload, path: `business-lines/${payload.id}` });

    return { businessLine: data.businessLine };
  }

  public async archive(id: string): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `business-lines/${id}`,
    });
  }

  public async unarchive(id: string): Promise<void> {
    await backendHttpClient.patch<void>({
      path: `business-lines/${id}/unarchive`,
    });
  }
}

export const businessLineService = new BusinessLineService();
